import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Shared React error boundary for app shells.
 * Pass `fallback` for app-specific copy; default UI is a recoverable page error.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mb-4 flex justify-center">
            <AlertTriangle className="h-12 w-12 text-warning-500" />
          </div>
          <h1 className="text-lg font-semibold text-slate-900">页面出错了</h1>
          <p className="mt-2 text-sm text-slate-600">
            某个组件在渲染时抛了异常。可尝试重试，或刷新页面。
          </p>
          {this.state.error && (
            <pre className="mt-4 max-h-48 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-left text-xs text-slate-700">
              {this.state.error.name}: {this.state.error.message}
            </pre>
          )}
          <div className="mt-6 flex justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              重试
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              重新加载
            </button>
          </div>
        </div>
      </div>
    );
  }
}
